import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/lib/partner-client";

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useSession((s) => s.login);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const result = await login(clientId, clientSecret);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? "login failed");
      return;
    }
    const from =
      (location.state as { from?: string } | undefined)?.from ?? "/";
    navigate(from, { replace: true });
  }

  return (
    <div className="mx-auto mt-24 max-w-md px-4">
      <h1 className="text-2xl font-semibold tracking-tight">Partner sign-in</h1>
      <p className="mt-2 text-sm text-neutral-500">
        Paste your OAuth client credentials from the Hub dev-portal onboarding
        email. Credentials never leave this tab — only a short-lived access
        token is stored in sessionStorage.
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <Field
          label="Client ID"
          value={clientId}
          onChange={setClientId}
          placeholder="prtn_..."
          required
        />
        <Field
          label="Client Secret"
          value={clientSecret}
          onChange={setClientSecret}
          type="password"
          placeholder="whsec_..."
          required
        />

        {error ? (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting || !clientId.trim() || !clientSecret.trim()}
          className="w-full rounded-lg bg-neutral-900 px-4 py-3 text-sm font-medium text-white transition hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-neutral-100 dark:text-neutral-900"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="mt-8 text-xs text-neutral-500">
        Don't have credentials yet? Contact your AppSumo partner manager — new
        partners are onboarded manually during the private beta.
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "password";
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        spellCheck={false}
        className="mt-1 w-full rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-sm focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/20 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900"
      />
    </label>
  );
}
